'use client';

import Link from 'next/link';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';

export default function PricingPage() {
  return (
    <>
      <MarketingNav />

      <main className="pt-[56px]">
        {/* Header */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <h1 className="text-[36px] sm:text-[44px] font-semibold text-[#1a1a1a] leading-[1.1] tracking-[-0.02em] mb-4">
              Pricing
            </h1>
            <p className="text-[18px] text-[#555] leading-[1.65] max-w-[560px]">
              Simple, transparent pricing. No hidden fees, no usage limits, no surprises.
            </p>
          </div>
        </section>

        {/* Plans */}
        <section className="bg-[#fafafa] border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <div className="grid md:grid-cols-2 gap-6 max-w-[800px] mx-auto">
              {/* Free Plan */}
              <div className="p-8 bg-white rounded-[8px] border border-[#e5e5e5]">
                <h2 className="text-[20px] font-semibold text-[#1a1a1a] mb-1">Free</h2>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-[36px] font-semibold text-[#1a1a1a]">£0</span>
                  <span className="text-[15px] text-[#888]">/ month</span>
                </div>
                <p className="text-[15px] text-[#555] leading-[1.55] mb-6">
                  Everything you need to get started with professional email.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    'Up to 3 email accounts on our server',
                    'Web client access',
                    'Desktop apps (macOS & Windows)',
                    'Unified inbox with folder management',
                    'Rich text email composer',
                    'Contact auto-complete',
                    'Fast search across all accounts',
                    'AES-256 encryption at rest',
                    'TLS 1.3 encryption in transit',
                    'No advertisements',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-[14px] text-[#555]">
                      <svg className="w-4 h-4 text-[#0078D4] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>

                <Link
                  href="/auth/register"
                  className="block w-full text-center px-5 py-3 bg-white text-[#333] text-[14px] font-semibold rounded-[4px] border border-[#d2d2d2] hover:bg-[#f5f5f5] transition-colors"
                >
                  Create free account
                </Link>
              </div>

              {/* Pro Plan */}
              <div className="p-8 bg-white rounded-[8px] border-2 border-[#0078D4] relative">
                <div className="absolute top-0 left-6 -translate-y-1/2 px-3 py-0.5 bg-[#0078D4] text-white text-[12px] font-semibold rounded-[3px]">
                  Coming soon
                </div>

                <h2 className="text-[20px] font-semibold text-[#1a1a1a] mb-1">Pro</h2>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-[36px] font-semibold text-[#1a1a1a]">£4.99</span>
                  <span className="text-[15px] text-[#888]">/ month</span>
                </div>
                <p className="text-[15px] text-[#555] leading-[1.55] mb-6">
                  For power users and teams who need more accounts and priority support.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    'Everything in Free, plus:',
                    'Unlimited email accounts',
                    'Priority email sync (faster polling)',
                    'Custom email signatures per account',
                    'Email scheduling (send later)',
                    'Priority support',
                    'Early access to new features',
                  ].map((item, i) => (
                    <li key={item} className={`flex items-start gap-2.5 text-[14px] ${i === 0 ? 'text-[#1a1a1a] font-semibold' : 'text-[#555]'}`}>
                      {i > 0 && (
                        <svg className="w-4 h-4 text-[#0078D4] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      )}
                      {item}
                    </li>
                  ))}
                </ul>

                <button
                  disabled
                  className="block w-full text-center px-5 py-3 bg-[#0078D4] text-white text-[14px] font-semibold rounded-[4px] opacity-60 cursor-not-allowed"
                >
                  Coming soon
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[800px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-8">
              Frequently asked questions
            </h2>

            <div className="space-y-8">
              {[
                {
                  q: 'Is TekSpert Mail really free?',
                  a: 'Yes. The free plan includes full access to the web client, desktop applications, and all core email features with up to 3 email accounts hosted on our server. There are no time limits, trial periods, or feature restrictions on the free plan.',
                },
                {
                  q: 'Can I use my own domain?',
                  a: 'Yes. TekSpert Mail supports custom domains. If you own a domain, we can host your email on our server so you can send and receive using your own professional email address. Contact support@tekspert.co.uk to set up a custom domain.',
                },
                {
                  q: 'Can I connect my Gmail or other email provider?',
                  a: 'No. TekSpert Mail is a full email hosting service — we host your email on our own servers. We do not support connecting to third-party email providers like Gmail, Yahoo, or Outlook.com. Your email address will be either @tekspertmail.co.uk or on your own custom domain hosted with us.',
                },
                {
                  q: 'Can I use TekSpert Mail for business?',
                  a: 'Absolutely. TekSpert Mail is designed for both individual and business use. You can set up multiple email addresses on your own custom domain and manage them all from a single account with our multi-account inbox.',
                },
                {
                  q: 'How do I cancel or delete my account?',
                  a: 'You can delete your TekSpert Mail account at any time from the Settings page in the application, or by contacting support@tekspert.co.uk. Account deletion permanently removes all stored data including your emails, contacts, and account credentials from our servers.',
                },
                {
                  q: 'Do you offer refunds for the Pro plan?',
                  a: 'The Pro plan is not yet available. When it launches, we will offer a 14-day money-back guarantee. If you are not satisfied with the Pro plan within the first 14 days, contact support for a full refund.',
                },
              ].map((item) => (
                <div key={item.q}>
                  <h3 className="text-[16px] font-semibold text-[#1a1a1a] mb-2">{item.q}</h3>
                  <p className="text-[15px] text-[#555] leading-[1.65]">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0078D4]">
          <div className="max-w-[800px] mx-auto px-6 py-14 text-center">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-white leading-tight mb-4">
              Start using TekSpert Mail today
            </h2>
            <p className="text-[16px] text-white/80 leading-[1.65] mb-6">
              No credit card required. Set up your account in under a minute.
            </p>
            <Link
              href="/auth/register"
              className="inline-flex px-6 py-3 bg-white text-[#0078D4] text-[15px] font-semibold rounded-[4px] hover:bg-[#f5f5f5] transition-colors"
            >
              Create free account
            </Link>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
